import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { DESIGN_REQUEST_OPTION_KEY, bugtracker } from '@99designs/design-services-common';
import { useBriefContext } from '../../../BriefContext';
import {
    ServiceOptionsUpdatedDocument,
    ServiceOptionsUpdatedMutation,
    ServiceOptionsUpdatedMutationVariables,
} from '../GridChoiceField/serviceOptionsUpdated.generated';
import { useUpdateField } from '../useUpdateField';
import { useBriefSetSelectChoiceMutation } from './briefSetSelectChoice.generated';

export function useUpdateSelectChoiceField(
    key: string,
    briefId?: string,
    initialValue?: string | null,
    isDesignType?: boolean
) {
    const client = useApolloClient();
    const { serviceProductOptions } = useBriefContext();
    const serviceProductOptionsRef = useRef(serviceProductOptions);

    const [setSelectChoice] = useBriefSetSelectChoiceMutation();
    const setSelectChoiceRef = useRef(setSelectChoice);

    useEffect(() => {
        serviceProductOptionsRef.current = serviceProductOptions;
    }, [serviceProductOptions]);

    useEffect(() => {
        setSelectChoiceRef.current = setSelectChoice;
    }, [setSelectChoice]);

    const stableSetSelectChoice = useCallback(
        (...args: Parameters<typeof setSelectChoice>) => setSelectChoiceRef.current(...args),
        []
    );

    const updateField = useUpdateField<string>({
        key,
        setField: stableSetSelectChoice,
        briefId,
        initialValue,
    });

    return useCallback(
        async (value: string) => {
            const result = await updateField(value);

            if (!isDesignType) {
                return;
            }

            if (!result?.data?.setField) {
                return;
            }

            const correlationId = result.data.setField.correlationId;
            if (!correlationId) {
                return;
            }

            const updatedOptions = (serviceProductOptionsRef.current ?? []).map((option) =>
                option.key === DESIGN_REQUEST_OPTION_KEY ? { key: option.key, value } : option
            );

            if (updatedOptions.length > 0) {
                try {
                    await client.mutate<
                        ServiceOptionsUpdatedMutation,
                        ServiceOptionsUpdatedMutationVariables
                    >({
                        mutation: ServiceOptionsUpdatedDocument,
                        variables: {
                            requestId: correlationId,
                            input: { options: updatedOptions },
                        },
                    });
                } catch (e) {
                    const error = e instanceof Error ? e : new Error(String(e));
                    bugtracker.notify(error);
                }
            }
        },
        [updateField, isDesignType, client]
    );
}
